// Reserve with Google (RwG) conversion token — client-side persistence.
//
// CIN-6442/CIN-6443: when a visitor lands on the widget via a Google-referred
// URL (…/<calendarId>?rwg_token=<value>), we capture the token so a later
// booking completion can be attributed and a conversion POSTed server-side.
//
// This localStorage cache is a best-effort convenience for return visits that
// arrive WITHOUT the query param (Safari ITP / iframe storage partitioning may
// isolate it). The authoritative 30-day store is the server-side
// RwgConversionToken row written on session create/patch.
//
// No PII is ever stored here — only the opaque token value and an expiry stamp.

const KEY_PREFIX = 'rwg_token';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const keyFor = (calendarId) => `${KEY_PREFIX}_${calendarId || 'global'}`;

// storeRwgToken persists the DECODED token verbatim (special chars like '=' are
// preserved — the caller passes the value from URLSearchParams.get, already
// decoded). Replace-on-new semantics: writing always overwrites any existing
// value and resets the 30-day expiry window.
export const storeRwgToken = (token, calendarId) => {
  if (!token) return;
  try {
    const payload = JSON.stringify({
      token,
      expires: Date.now() + THIRTY_DAYS_MS,
    });
    window.localStorage.setItem(keyFor(calendarId), payload);
  } catch (e) {
    // localStorage unavailable (blocked/partitioned in a third-party iframe).
    // Non-fatal: the server-side row is the source of truth.
  }
};

// getRwgToken returns the stored token only if present and unexpired. Expired
// or malformed entries are purged on read; returns null when nothing valid
// is stored (e.g. a normal non-Google visitor).
export const getRwgToken = (calendarId) => {
  const key = keyFor(calendarId);
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed.token !== 'string' ||
      typeof parsed.expires !== 'number'
    ) {
      window.localStorage.removeItem(key);
      return null;
    }

    if (parsed.expires <= Date.now()) {
      window.localStorage.removeItem(key);
      return null;
    }

    return parsed.token;
  } catch (e) {
    // Corrupt JSON or storage access error — purge and treat as absent.
    try {
      window.localStorage.removeItem(key);
    } catch (removeErr) {
      // ignore
    }
    return null;
  }
};
